import React from 'react';

import classNames from 'classnames';

import AmbaDropdown from '@/components/global/AmbaDropdown';
import AmbaInput from '@/components/global/AmbaInput';
import Button from '@/components/global/Button';
import { getInputType } from '@/helpers/form';
import { isReCaptchaValid } from '@/helpers/utilities';
import useCustomFormFields from '@/hooks/useCustomFormFields';
import { useTranslation } from '@/i18n';
import { ReCaptchaAction, RegisterFormData, RegisterResponse } from '@/models/authModels';
import { FormField, FormFieldType, RegisterCustomization, SelectedFormFields } from '@/models/customizationModels';
import { AmbaDropdownItem } from '@/models/globalModels';

import styles from './index.module.scss';
import useFormFields from './useFormFields';

export type RegisterFormProps = {
  customization: RegisterCustomization;
  errorMessage?: string;
  formFields: SelectedFormFields;
  isLoading?: boolean;
  onSubmit: (form: RegisterFormData) => void;
  validationResponse?: RegisterResponse;
};

const RegisterForm = ({ onSubmit, isLoading = false, formFields, customization, validationResponse }: RegisterFormProps) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = React.useState<RegisterFormData>({});
  const [enabledFormFields, setEnabledFormFields] = React.useState<FormField[]>([]);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [invalidFields, setInvalidFields] = React.useState<string[]>([]);
  const customFields = useCustomFormFields();
  const BaseFormFieldItems = useFormFields();

  const states = customization?.ambassador_state_choices;
  const stateType = customization?.ambassador_state_type;
  const groups = customization?.groups;
  const isRecaptchaEnabled = !!customization?.recaptcha_enabled;

  // Event Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = { id: e.target.id, value: e.target.value };
    setFormData({ ...formData, [data.id]: data.value });
  };

  const handleDropdownChange = (selected: AmbaDropdownItem) => {
    const data = { name: selected.itemCategory as keyof typeof formData, value: selected.itemValue };
    const field = enabledFormFields.find((field) => field.fieldName === data.name);

    if (field) {
      handleValidField(field);
    }

    setFormData({ ...formData, [data.name]: data.value });
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = { name: e.target.name, value: e.target.value };
    const field = enabledFormFields.find((field) => field.fieldName === data.name);

    if (field) {
      handleValidField(field);
    }

    setFormData({ ...formData, [data.name]: data.value });
  };

  const handleInvalidField = (field: FormField) => {
    if (!invalidFields.includes(field.fieldName)) {
      setInvalidFields([...invalidFields, field.fieldName]);
    }
  };

  const handleValidField = (field: FormField) => {
    if (invalidFields.includes(field.fieldName)) {
      const invalids = invalidFields.filter((i) => i !== field.fieldName);
      setInvalidFields(invalids);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    // executes recaptcha validation if enabled
    if (isRecaptchaEnabled) {
      try {
        const recaptcha = await isReCaptchaValid(ReCaptchaAction.REGISTER);
        if (!recaptcha?.isValid) return; // TODO: Show alert msg for non-verified recaptcha
        setFormData((prev) => ({ ...prev, recaptcha: recaptcha?.token }));
      } catch (error) {
        alert('Error validating recaptcha: ' + error); // TODO: After the above alert is implemented, use it for this
      }
    }

    if (customization.set_contact_loyalty_on_register) {
      setFormData((prev) => ({ ...prev, is_loyalty: true }));
    }

    onSubmit({ ...formData });
  };

  const getValidationMessage = (key: string) => {
    // checks for non specific field validation errors
    const isDetailedError = validationResponse?.detail && validationResponse?.detail.includes(key);
    if ((isFieldInvalid(key) || isDetailedError) && validationResponse) {
      // if error detail contains field name it uses a generic field validation msg
      if (isDetailedError) return t('formValidation.formFieldInvalidGeneric');
      // returns a specific field validation msg from API
      return validationResponse[key as keyof RegisterResponse][0];
    } else return undefined;
  };

  const isFieldInvalid = (fieldName: string) => {
    if (!validationResponse || validationResponse == undefined) return false;
    return validationResponse[fieldName as keyof RegisterResponse] ? true : false;
  };

  // hydrates base field values with customer selected form fields
  const getHydratedFields = React.useCallback(
    (allFields: FormField[]) => {
      if (!allFields || allFields.length < 1) return [] as FormField[];
      const hydrated = allFields.map(({ fieldType, ...field }) => {
        const fieldLabel = formFields[field.fieldLabelKey as keyof typeof formFields];
        // conditionally converts choice fields based on customization or no options
        const isStateDefaultWithNoData = field.fieldName === 'state' && stateType === 'default' && states?.length === 0;
        const isGroupsWithNoData = field.fieldName === 'groups' && groups?.length === 0;
        const forceInput = fieldType === FormFieldType.CHOICE && (isStateDefaultWithNoData || isGroupsWithNoData);
        return {
          ...field,
          fieldType: forceInput ? FormFieldType.INPUT : fieldType,
          showFieldValue: formFields[field.showFieldKey as keyof typeof formFields],
          fieldPositionValue: formFields[field.fieldPositionKey as keyof typeof formFields],
          fieldLabelValue: fieldLabel ? fieldLabel : field.fieldLabelValue, //if no custom label, sets default from base fields
          isFieldRequiredValue: formFields[field.isFieldRequiredKey as keyof typeof formFields],
        };
      });

      return hydrated as FormField[];
    },
    [formFields, groups?.length, stateType, states?.length],
  );

  // returns only enabled form fields from company customization
  const getEnabledFields = React.useCallback(
    (baseFields: FormField[]) => {
      if (!baseFields || baseFields.length < 1) return [] as FormField[];
      const hydrated: FormField[] = getHydratedFields(baseFields);
      return hydrated
        .filter((i): i is FormField => (i.showFieldValue ? true : false))
        .sort((a, b) => {
          return (a.fieldPositionValue || 0) - (b.fieldPositionValue || 0);
        });
    },
    [getHydratedFields],
  );

  const getOptionsByFieldName = (fieldName: string): AmbaDropdownItem[] => {
    if (fieldName === 'groups') return groups;
    if (fieldName === 'state' && stateType === 'choice') {
      return states;
    }
    return []; // custom dropdown renders as an input if no options
  };

  const getFieldByType = (field: FormField) => {
    switch (field.fieldType) {
      case FormFieldType.INPUT:
      case FormFieldType.DATE:
        return (
          <AmbaInput
            ariaDescribedBy=""
            extraLabelClass="c-waterloo"
            hasLiveValidation={field.isFieldRequiredValue || field.hasLiveValidation}
            hasValidation={field.isFieldRequiredValue}
            id={field.fieldName}
            isRequired={field.isFieldRequiredValue}
            labelText={field.fieldLabelValue}
            onChange={handleInputChange}
            onValidationFail={() => handleInvalidField(field)}
            onValidationSuccess={() => handleValidField(field)}
            type={getInputType(field)}
            validationMessage={getValidationMessage(field.fieldName)}
            validator={(value: any) => field.validator?.(value, field.isFieldRequiredValue)}
            value={formData[field.fieldName as keyof typeof formData]}
          />
        );

      case FormFieldType.CHOICE:
        return (
          <AmbaDropdown
            bgColor="#F5F7FC"
            extraLabelClass="c-waterloo"
            id={field.fieldName}
            isRequired={field.isFieldRequiredValue}
            labelText={field.fieldLabelValue}
            onClick={handleDropdownChange}
            options={getOptionsByFieldName(field.fieldName)}
            placeHolder="Select"
            validationMessage={getValidationMessage(field.fieldName)}
            value={formData[field.fieldName as keyof typeof formData]}
            width="100%"
          />
        );

      case FormFieldType.BOOLEAN:
        return (
          <>
            <div className="f-15 f-roboto-semiBold c-waterloo mb-1">
              {field.fieldLabelValue} {field.isFieldRequiredValue && '*'}
            </div>

            <div className="form-check">
              <label className="form-check-label">
                <input className="form-check-input" name={field.fieldName} onChange={handleRadioChange} type="radio" value={1} />
                Yes
              </label>
            </div>

            <div className="form-check">
              <label className="form-check-label">
                <input className="form-check-input" name={field.fieldName} onChange={handleRadioChange} type="radio" value={0} />
                No
              </label>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  React.useEffect(() => {
    // merges base form fields with additional custom
    const allFields = BaseFormFieldItems.concat(customFields);
    const enabled = getEnabledFields(allFields);
    setEnabledFormFields(enabled);
  }, [customFields, getEnabledFields]);

  React.useEffect(() => {
    setIsFormValid(invalidFields.length === 0);
  }, [invalidFields]);

  React.useEffect(() => {
    setInvalidFields(enabledFormFields.filter(({ isFieldRequiredValue }) => isFieldRequiredValue).map(({ fieldName }) => fieldName));
  }, [enabledFormFields]);

  // ensures that recaptcha script is loaded when enabled
  React.useEffect(() => {
    if (!isRecaptchaEnabled) return;
    const initializeReCaptcha = async () => {
      console.log('Initializing ReCaptcha...');
      await new Promise((resolve) => {
        // @ts-ignore
        if (typeof window?.grecaptcha !== 'undefined') {
          // @ts-ignore
          grecaptcha?.enterprise?.ready(() => {
            console.log('ReCaptcha ready.');
            resolve({});
          });
        }
      });
    };

    initializeReCaptcha();
  }, [isRecaptchaEnabled]);

  return (
    <div className="RegisterForm row my-2">
      <div className="col-12">
        <form className="needs-validation" onSubmit={handleSubmit} noValidate>
          <div className="mt-2">
            <div className="container">
              <div className="form-group">
                {enabledFormFields.map((field) => (
                  <div key={field.fieldName} className="mb-2">
                    {getFieldByType(field)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <hr />

          <div className="submitWrapper px-2 my-4">
            <div className="termsConditions my-4 form-check">
              <input
                className="form-check-input"
                id="terms"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, ['terms']: e.target.checked })}
                type="checkbox"
              />
              <label
                className="form-check-label f-roboto-semiBold"
                dangerouslySetInnerHTML={{ __html: customization.terms_text }}
                htmlFor="terms"
              />
            </div>
            <Button
              className={classNames(styles.registerBtn, styles.custom, 'w-100')}
              disabled={isLoading || !isFormValid || !formData.terms}
              loading={isLoading}
              text={customization.registration_page_button_text}
              isSubmit
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
